import type {
  DocumentStatus,
  ListQuery,
  PaymentStatus,
  PurchaseOrderStatus,
  QuotationStatus,
} from "@/api/types";
import { isGregorianMonth, isMonthValue, monthRange, monthsOf } from "@/lib/calendar";

/**
 * The list state that lives in the URL.
 *
 * In the URL, not in component state, and that is the whole design: a filtered list is a thing
 * people send each other. "The three overdue ones from Shrawan" should be a link, and a back button
 * should return to the list you were looking at rather than an unfiltered one.
 */
export interface ListSearch {
  q?: string;
  status?: DocumentStatus | QuotationStatus | PurchaseOrderStatus | "all";
  payment?: PaymentStatus | "all";
  fy?: string;
  month?: string;
  from?: string;
  to?: string;
  min?: string;
  page?: number;
  size?: 25 | 50 | 100;
}

export const PAGE_SIZES = [25, 50, 100] as const;

const DEFAULT_SIZE = 25;

/**
 * Reads the search params TanStack Router hands us.
 *
 * Everything is optional and everything unrecognised is dropped. A URL is user input — somebody
 * will hand-edit one, and a truncated or invented parameter must produce a sane list rather than a
 * crash or, worse, a filter nobody can see in the bar.
 */
export function parseListSearch(raw: Record<string, unknown>): ListSearch {
  const text = (key: string): string | undefined => {
    const value = raw[key];
    // The router reads `?min=5000` as a number, so a shared link or a reload arrives as one.
    if (typeof value !== "string" && typeof value !== "number") return undefined;
    const trimmed = String(value).trim();
    return trimmed.length > 0 ? trimmed : undefined;
  };

  const oneOf = <T extends string>(key: string, allowed: readonly T[]): T | undefined => {
    const value = text(key);
    return value !== undefined && (allowed as readonly string[]).includes(value)
      ? (value as T)
      : undefined;
  };

  const page = Number(raw["page"]);
  const size = Number(raw["size"]);

  return {
    q: text("q"),
    // The unions from api/types.ts, spelled out rather than derived: a value the API does not
    // know must not reach it, and a typo here is a compile error rather than a silent filter.
    status: oneOf<DocumentStatus | QuotationStatus | PurchaseOrderStatus | "all">("status", [
      "all",
      "draft",
      "submitted",
      "verified",
      "approved",
      "void",
      "sent",
      "accepted",
      "declined",
      "expired",
      "converted",
      "confirmed",
      "partiallyReceived",
      "received",
      "billed",
      "cancelled",
    ]),
    payment: oneOf<PaymentStatus | "all">("payment", [
      "all",
      "unpaid",
      "partiallyPaid",
      "paid",
      "overdue",
    ]),
    fy: text("fy"),
    // A month is "2026-08", or the dates a month of another calendar covers. Anything else is dropped rather than passed to the API, which would
    // otherwise filter on a string nobody typed.
    month: isMonthValue(text("month") ?? "") ? text("month") : undefined,
    from: isoDate(text("from")),
    to: isoDate(text("to")),
    min: /^\d+(\.\d+)?$/.test(text("min") ?? "") ? text("min") : undefined,
    page: Number.isInteger(page) && page > 1 ? page : undefined,
    size: PAGE_SIZES.includes(size as (typeof PAGE_SIZES)[number])
      ? (size as ListSearch["size"])
      : undefined,
  };
}

function isoDate(value: string | undefined): string | undefined {
  return value !== undefined && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;
}

/**
 * Drops empty values so the URL carries only what is actually filtering.
 *
 * `?q=&status=all&page=1` and `?` describe the same list, and only one of them is a link worth
 * sending. It also keeps the query key stable, so TanStack Query does not refetch because a
 * parameter went from absent to empty.
 */
export function toSearchParams(search: ListSearch): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (search.q) out["q"] = search.q;
  if (search.status && search.status !== "all") out["status"] = search.status;
  if (search.payment && search.payment !== "all") out["payment"] = search.payment;
  if (search.fy) out["fy"] = search.fy;
  if (search.month) out["month"] = search.month;
  if (search.from) out["from"] = search.from;
  if (search.to) out["to"] = search.to;
  if (search.min) out["min"] = search.min;
  if (search.page && search.page > 1) out["page"] = search.page;
  if (search.size && search.size !== DEFAULT_SIZE) out["size"] = search.size;
  return out;
}

/** A fiscal year's filter value is its AD range, so the URL needs nothing else to mean it. */
export const fiscalYearValue = (year: { start: string; end: string }) =>
  `${year.start}..${year.end}`;

function fiscalYearRange(value: string | undefined) {
  const match = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/.exec(value ?? "");
  return match ? { from: match[1]!, to: match[2]! } : undefined;
}

export { monthsOf };

/** The API's shape. The URL is the user's vocabulary; this is the contract's. */
export function toListQuery(search: ListSearch): ListQuery {
  // The API takes a date range. A typed range wins, then a month, then the fiscal year.
  const range = search.month ? monthRange(search.month) : fiscalYearRange(search.fy);
  return {
    q: search.q,
    month: search.month && isGregorianMonth(search.month) ? search.month : undefined,
    from: search.from || range?.from,
    to: search.to || range?.to,
    minAmount: search.min,
    status: search.status,
    payment: search.payment,
    pageSize: search.size ?? DEFAULT_SIZE,
  };
}

/**
 * Whether anything is filtering. Drives the "Clear filters" button and, more importantly, tells an
 * empty list which of two things it is: "nothing here yet" or "nothing matches" — which need
 * different words and different next actions.
 */
export function hasActiveFilters(search: ListSearch): boolean {
  const { page: _page, size: _size, ...filters } = search;
  return Object.entries(filters).some(
    ([, value]) => value !== undefined && value !== "" && value !== "all",
  );
}

/** Clears the filters and returns to the first page, keeping the page size the user chose. */
export function clearFilters(search: ListSearch): ListSearch {
  return { size: search.size };
}

/** A date range typed by hand wins over a month picked from the list; both at once is ambiguous. */
export function isCustomRange(search: ListSearch): boolean {
  return search.from !== undefined || search.to !== undefined;
}
